import { Check, Target, X } from "lucide-react";
import type { ImproveResumeLlmOutput } from "@super-resume-new/resume-engine";
import { Badge } from "@super-resume-new/ui/components/badge";
import { Meter, MeterIndicator, MeterTrack } from "@super-resume-new/ui/components/meter";
import { cn } from "@super-resume-new/ui/lib/utils";

type Coverage = ImproveResumeLlmOutput["coverage"];
type TargetProfile = ImproveResumeLlmOutput["target_profile"];
type Tier = "core" | "secondary" | "tail";

type Props = {
  coverage?: Coverage;
  target_profile?: TargetProfile;
  className?: string;
};

/** core is what actually decides the screen — it must read loudest. */
function TierBadge({ tier }: { tier: Tier }) {
  return (
    <Badge
      variant={tier === "core" ? "default" : tier === "secondary" ? "secondary" : "outline"}
      className={
        tier === "core"
          ? "text-[10px] py-0 px-1.5 border-brand bg-primary text-white"
          : "text-[10px] py-0 px-1.5 text-muted-foreground"
      }
    >
      {tier}
    </Badge>
  );
}

export function CoverageScorecard({ coverage, target_profile, className }: Props) {
  const found = coverage?.found ?? [];
  const missing = coverage?.missing ?? [];
  const total = found.length + missing.length;
  if (total === 0) return null;

  const soft = target_profile?.soft_requirements ?? [];
  const percent = Math.round((found.length / total) * 100);

  return (
    <section
      className={cn(
        "flex flex-col gap-4 rounded-xl border border-brand/25 bg-brand/5 p-4 shadow-sm",
        className,
      )}
    >
      <div className="flex flex-col gap-2">
        <div className="flex flex-wrap items-baseline justify-between gap-2">
          <h3 className="flex items-center gap-1.5 text-sm font-semibold text-foreground">
            <Target size={16} className="text-brand-foreground" aria-hidden="true" />
            Keyword coverage
          </h3>
          <p className="text-sm font-semibold tabular-nums text-foreground">
            {found.length} / {total}{" "}
            <span className="font-medium text-muted-foreground">target keywords</span>
          </p>
        </div>
        <Meter value={percent} aria-label="Target keyword coverage">
          <MeterTrack className="rounded-full">
            <MeterIndicator className="bg-primary" />
          </MeterTrack>
        </Meter>
      </div>

      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 flex-1">
        <div className="flex flex-col gap-1.5">
          <h4 className="flex items-center gap-1.5 text-xs font-semibold text-emerald-500">
            <Check size={14} aria-hidden="true" />
            Placed in a bullet ({found.length})
          </h4>
          <ul className="flex flex-col gap-1.5 max-h-[220px] overflow-y-auto pr-1">
            {found.map((f, i) => (
              <li
                key={`${f.keyword}-${i}`}
                className="rounded-lg border border-border bg-card p-2 flex flex-col gap-1"
              >
                <div className="flex flex-wrap items-center gap-1.5">
                  <span className="text-xs font-medium text-foreground">{f.keyword}</span>
                  <TierBadge tier={f.tier} />
                </div>
                <p className="text-[11px] leading-snug text-muted-foreground">{f.placed_in}</p>
              </li>
            ))}
            {found.length === 0 && (
              <li className="text-[11px] text-muted-foreground">Nothing placed yet.</li>
            )}
          </ul>
        </div>

        <div className="flex flex-col gap-1.5">
          <h4 className="flex items-center gap-1.5 text-xs font-semibold text-amber-700 dark:text-amber-400">
            <X size={14} aria-hidden="true" />
            Not covered ({missing.length})
          </h4>
          <ul className="flex flex-col gap-1.5 max-h-[220px] overflow-y-auto pr-1">
            {missing.map((m, i) => (
              <li
                key={`${m.keyword}-${i}`}
                className="rounded-lg border border-border bg-card p-2 flex flex-col gap-1"
              >
                <div className="flex flex-wrap items-center gap-1.5">
                  <span className="text-xs font-medium text-foreground">{m.keyword}</span>
                  <TierBadge tier={m.tier} />
                </div>
                <p className="text-[11px] leading-snug text-muted-foreground">{m.why_not}</p>
              </li>
            ))}
            {missing.length === 0 && (
              <li className="text-[11px] text-muted-foreground">Every target keyword landed.</li>
            )}
          </ul>
        </div>
      </div>

      {soft.length > 0 && (
        <div className="flex flex-wrap items-center gap-1.5 border-t border-border pt-3">
          <span className="text-[11px] font-semibold text-muted-foreground">
            Soft requirements:
          </span>
          {soft.map((s, i) => (
            <Badge key={`${s}-${i}`} variant="outline" className="text-[10px] py-0 px-1.5">
              {s}
            </Badge>
          ))}
        </div>
      )}
    </section>
  );
}
